import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import {
  Card,
  Stack,
  Container,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  MenuItem,
  Grid
} from "@mui/material";
import { Visibility, Add } from "@mui/icons-material";
import Page from "../components/Page";
import { fetchallcitylist } from "src/utils/common";
import CustomLoader from "src/commonComponents/CustomLoader";

export default function AdminCitySupportList() {
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("all");

  const stateOptions = useMemo(() => {
    const states = [...new Set(cities.map((c) => c.state_name).filter(Boolean))];
    return states.sort();
  }, [cities]);

  const filteredCities = useMemo(() => {
    return cities.filter((c) => {
      const matchSearch = !search || c.city_name?.toLowerCase().includes(search.toLowerCase());
      const matchState = stateFilter === "all" || c.state_name === stateFilter;
      return matchSearch && matchState;
    });
  }, [cities, search, stateFilter]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const cityData = await fetchallcitylist();
      if (cityData) setCities(cityData);
      setLoading(false);
    })();
  }, []);

  const columns = [
    {
      name: "City Name",
      selector: (row) => row.city_name,
      sortable: true,
      wrap: true
    },
    {
      name: "State",
      selector: (row) => row.state_name || "-",
      sortable: true,
      wrap: true
    },
    {
      name: "Support Profiles",
      selector: (row) => row.profile_count || 0,
      sortable: true,
      width: "160px"
    },
    {
      name: "Status",
      selector: (row) => (
        <Chip
          label={row.c_status == 0 ? "Active" : "Deactive"}
          size="small"
          sx={{
            borderRadius: "9999px",
            fontWeight: 600,
            fontSize: 12,
            bgcolor: row.c_status == 0 ? "success.lighter" : "grey.200",
            color: row.c_status == 0 ? "success.dark" : "text.disabled"
          }}
        />
      ),
      width: "120px"
    },
    {
      name: "Action",
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="View Profiles">
            <IconButton
              size="small"
              sx={{ borderRadius: 2 }}
              onClick={() => navigate(`/city-support/list/${row.city_id}`)}
            >
              <Visibility color="primary" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Profile">
            <IconButton
              size="small"
              sx={{ borderRadius: 2 }}
              onClick={() => navigate("/city-support/add", { state: { preselectedCityId: row.city_id, preselectedCityName: row.city_name } })}
            >
              <Add color="success" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
      width: "120px"
    }
  ];

  return (
    <Page title="City Support | LVB Privilege">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Box>
            <Typography variant="h4" gutterBottom>
              City Support
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a city to manage its helpline profiles
            </Typography>
          </Box>
        </Stack>

        <Card sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="flex-end" mb={2}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Stack spacing={0.5}>
                <Typography variant="caption" sx={{ fontWeight: "bold", ml: 0.5 }}>
                  Search City
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  autoComplete="off"
                  placeholder="Search city..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Stack spacing={0.5}>
                <Typography variant="caption" sx={{ fontWeight: "bold", ml: 0.5 }}>
                  Select State
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                >
                  <MenuItem value="all">All States</MenuItem>
                  {stateOptions.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Grid>
          </Grid>
          <DataTable
            title="City List"
            columns={columns}
            data={filteredCities}
            pagination
            highlightOnHover
            progressPending={loading}
            progressComponent={<CustomLoader />}
            noDataComponent={
              <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                No cities available
              </Typography>
            }
          />
        </Card>
      </Container>
    </Page>
  );
}
